import { checkOrgNpiAvailable, isNpiFormatValidForLookup } from '@/services/npiInUseCheck'
import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_DEBOUNCE_MS = 400

export interface UseOrgNpiAvailabilityOptions {
  npi: string
  excludeUserId?: string | null
  baselineNpi?: string | null
  enabled?: boolean
  debounceMs?: number
}

export interface UseOrgNpiAvailabilityResult {
  duplicateError: string | null
  isChecking: boolean
  hasDuplicate: boolean
  validateOnBlur: () => void
  validateBeforeSubmit: () => Promise<{ ok: true } | { ok: false; message: string }>
  clearDuplicateError: () => void
}

function shouldScheduleLookup(npi: string, baselineNpi?: string | null): boolean {
  const trimmed = npi.trim()
  if (!trimmed) return false
  if (baselineNpi != null && trimmed === baselineNpi.trim()) return false
  return trimmed.length === 10 && isNpiFormatValidForLookup(trimmed)
}

function shouldLookupOnBlur(npi: string, baselineNpi?: string | null): boolean {
  const trimmed = npi.trim()
  if (!trimmed) return false
  if (baselineNpi != null && trimmed === baselineNpi.trim()) return false
  return isNpiFormatValidForLookup(trimmed)
}

/**
 * Debounced duplicate-NPI guard for org-admin user management and self-service profile edits.
 * Calls GET /auth/users/npi-in-use after 10 digits (debounced) or on blur.
 */
export function useOrgNpiAvailability({
  npi,
  excludeUserId,
  baselineNpi,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseOrgNpiAvailabilityOptions): UseOrgNpiAvailabilityResult {
  const [duplicateError, setDuplicateError] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)

  const clearDuplicateError = useCallback(() => {
    setDuplicateError(null)
  }, [])

  const runCheck = useCallback(
    async (npiValue: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const requestId = ++requestIdRef.current

      setIsChecking(true)
      try {
        const result = await checkOrgNpiAvailable({
          npi: npiValue,
          excludeUserId,
          skipWhenUnchangedFrom: baselineNpi,
          signal: controller.signal,
        })

        if (requestId !== requestIdRef.current) {
          return { ok: true }
        }

        if (!result.ok) {
          setDuplicateError(result.message)
        } else {
          setDuplicateError(null)
        }
        return result
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
          return { ok: true }
        }
        if (requestId !== requestIdRef.current) {
          return { ok: true }
        }
        const message = e instanceof Error ? e.message : 'Failed to check NPI availability.'
        setDuplicateError(message)
        return { ok: false, message }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsChecking(false)
        }
      }
    },
    [baselineNpi, excludeUserId]
  )

  useEffect(() => {
    if (!enabled) {
      setDuplicateError(null)
      setIsChecking(false)
      return
    }

    const trimmed = npi.trim()
    if (!trimmed || (baselineNpi != null && trimmed === baselineNpi.trim())) {
      setDuplicateError(null)
      setIsChecking(false)
      abortRef.current?.abort()
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      return
    }

    if (!shouldScheduleLookup(npi, baselineNpi)) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      setDuplicateError(null)
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      void runCheck(npi)
    }, debounceMs)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [baselineNpi, debounceMs, enabled, npi, runCheck])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const validateOnBlur = useCallback(() => {
    if (!enabled) return
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (!shouldLookupOnBlur(npi, baselineNpi)) {
      if (!npi.trim() || (baselineNpi != null && npi.trim() === baselineNpi.trim())) {
        setDuplicateError(null)
      }
      return
    }
    void runCheck(npi)
  }, [baselineNpi, enabled, npi, runCheck])

  const validateBeforeSubmit = useCallback(async () => {
    if (!enabled) return { ok: true as const }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    const trimmed = npi.trim()
    if (!trimmed) return { ok: true as const }
    if (baselineNpi != null && trimmed === baselineNpi.trim()) {
      return { ok: true as const }
    }
    if (!isNpiFormatValidForLookup(trimmed)) {
      return { ok: false as const, message: 'NPI must be numeric and at most 10 digits.' }
    }
    return runCheck(npi)
  }, [baselineNpi, enabled, npi, runCheck])

  return {
    duplicateError,
    isChecking,
    hasDuplicate: Boolean(duplicateError),
    validateOnBlur,
    validateBeforeSubmit,
    clearDuplicateError,
  }
}
